import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search as SearchIcon, Monitor, LayoutGrid, Building2, Users, Globe } from 'lucide-react';
import { useSearch } from '@/hooks/queries/useSearch';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { ObjectType, ObjectStatus, SearchParams, SearchResult } from '@arkitekt/shared';

const objectTypeIcons: Record<ObjectType, React.ReactNode> = {
  Application: <Monitor className="h-4 w-4" />,
  Technology: <LayoutGrid className="h-4 w-4" />,
  BusinessCapability: <Building2 className="h-4 w-4" />,
  Person: <Users className="h-4 w-4" />,
  Organization: <Globe className="h-4 w-4" />,
};

const objectTypeLabels: Record<ObjectType, string> = {
  Application: 'Applikasjon',
  Technology: 'Teknologi',
  BusinessCapability: 'Forretningskapabilitet',
  Person: 'Person',
  Organization: 'Organisasjon',
};

const statusLabels: Record<ObjectStatus, string> = {
  draft: 'Utkast',
  active: 'Aktiv',
  deprecated: 'Utfaset',
  retired: 'Pensjonert',
};

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [selectedTypes, setSelectedTypes] = useState<ObjectType[]>([]);

  const searchApiParams: SearchParams | null = query
    ? {
        q: query,
        types: selectedTypes.length > 0 ? selectedTypes : undefined,
        limit: 50,
      }
    : null;

  const { data, isLoading, error } = useSearch(searchApiParams);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    }
  };

  const toggleType = (type: ObjectType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  // Update query from URL
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
    }
  }, [searchParams]);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Search form */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Søk etter arkitekturobjekter..."
            className="pl-12 h-12 text-lg"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </form>

      {/* Type filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span className="text-sm text-muted-foreground py-2">Filtrer på type:</span>
        {(['Application', 'Technology', 'BusinessCapability', 'Person', 'Organization'] as ObjectType[]).map(
          (type) => (
            <Button
              key={type}
              variant={selectedTypes.includes(type) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleType(type)}
            >
              {objectTypeIcons[type]}
              <span className="ml-2">{objectTypeLabels[type]}</span>
            </Button>
          ),
        )}
      </div>

      {/* Results */}
      {!query ? (
        <div className="text-center py-12">
          <SearchIcon className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-medium">Søk i arkitekturen</h2>
          <p className="mt-2 text-muted-foreground">
            Bruk søkefeltet over for å finne applikasjoner, teknologier, kapabiliteter og mer
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
          <p>Søket feilet. Prøv igjen.</p>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12">
          <SearchIcon className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-medium">Ingen treff</h2>
          <p className="mt-2 text-muted-foreground">
            Fant ingen objekter som matcher "{query}"
          </p>
        </div>
      ) : (
        <>
          {/* Facets */}
          {data?.facets && (
            <div className="mb-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Typer:</span>
                {data.facets.types.map((facet) => (
                  <Badge key={facet.value} variant="secondary">
                    {objectTypeLabels[facet.value]} ({facet.count})
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Arbeidsområder:</span>
                {data.facets.spaces.slice(0, 3).map((facet) => (
                  <Badge key={facet.id} variant="outline">
                    {facet.name} ({facet.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Results list */}
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {data?.pagination.total} treff for "{query}"
            </p>
            {data?.data.map((result) => (
              <SearchResultItem key={result.id} result={result} />
            ))}
          </div>

          {/* Pagination */}
          {data?.pagination && data.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={data.pagination.page === 1}
                onClick={() =>
                  setSearchParams({
                    q: query,
                    page: String(data.pagination.page - 1),
                  })
                }
              >
                Forrige
              </Button>
              <span className="py-2 px-4">
                Side {data.pagination.page} av {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={data.pagination.page === data.pagination.totalPages}
                onClick={() =>
                  setSearchParams({
                    q: query,
                    page: String(data.pagination.page + 1),
                  })
                }
              >
                Neste
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SearchResultItem({ result }: { result: SearchResult }) {
  return (
    <Link to={`/spaces/${result.spaceKey}/objects/${result.id}`}>
      <Card className="transition-shadow hover:shadow-md">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted shrink-0">
              {objectTypeIcons[result.type]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium truncate">{result.name}</h3>
                <Badge variant="outline" className="shrink-0">
                  {objectTypeLabels[result.type]}
                </Badge>
                <Badge variant="secondary" className="shrink-0">
                  {statusLabels[result.status]}
                </Badge>
              </div>
              {result.description && (
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{result.description}</p>
              )}
              {result.highlight && (
                <p
                  className="mt-2 text-sm bg-yellow-50 px-2 py-1 rounded"
                  dangerouslySetInnerHTML={{ __html: result.highlight }}
                />
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Arbeidsområde: {result.spaceName}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
